import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buildLabelsLabQuotePdfBlob, downloadLabelsLabQuotePdfs } from '../helpers/generateLabelsLabQuotePdf';
import PdfLayoutJoystick from './PdfLayoutJoystick';
import PdfCustomTableEditor from './PdfCustomTableEditor';
import CollapsibleSection from './CollapsibleSection';
import {
  createEmptyPricingRow,
  DEFAULT_BOX_TURNAROUND,
  DEFAULT_LABEL_LEAD_TIME,
  recalcPricingRow,
  recalcShippingRow,
  syncPayloadFromEdits,
} from '../helpers/quotePdfPayloadUtils';
import { createCustomTable, normalizeCustomTables } from '../helpers/pdfCustomTableUtils';
import { normalizePdfLayout, nudgePdfLayout, resizePdfLayoutSection, resetPdfLayoutSection } from '../helpers/pdfLayoutUtils';
import { getProductTabLabel } from '../helpers/buildPdfPayloadFromText';

const inputClass = 'w-full border border-gray-300 rounded px-2 py-1.5 text-sm';
const labelClass = 'block text-xs font-semibold text-gray-600 mb-1';

function QuotePdfPreviewPanel({ payload, onPayloadChange, onBack }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeProductIndex, setActiveProductIndex] = useState(0);

  const products = payload.products || [];
  const multiProduct = products.length > 1;
  const product = products[activeProductIndex] || products[0] || {};
  const fields = product.fields || {};
  const isBox = product.productType === 'Boxes';
  const isLabel = product.productType === 'Labels';
  const showShippingTable = product.showShippingTable !== false
    && (product.showShippingTable === true || payload.showShippingTable !== false);
  const pdfLayout = normalizePdfLayout(payload.pdfLayout);
  const customTables = useMemo(
    () => normalizeCustomTables(payload.pdfCustomTables),
    [payload.pdfCustomTables],
  );

  const nudgeLayout = (section, dx, dy) => {
    patchPayload({ pdfLayout: nudgePdfLayout(payload.pdfLayout, section, dx, dy) });
  };

  const resizeLayout = (section, dw, dh) => {
    patchPayload({ pdfLayout: resizePdfLayoutSection(payload.pdfLayout, section, dw, dh) });
  };

  const resetLayoutSection = (section) => {
    patchPayload({ pdfLayout: resetPdfLayoutSection(payload.pdfLayout, section) });
  };

  const getPreviewBlobOptions = useCallback((data) => {
    const synced = syncPayloadFromEdits(data);
    const count = synced.products?.length || 0;
    if (count <= 1) return { synced, blobOpts: {} };
    if (synced.pdfOutputMode === 'separate') {
      return { synced, blobOpts: { productIndices: [activeProductIndex] } };
    }
    return { synced, blobOpts: {} };
  }, [activeProductIndex]);

  const refreshPreview = useCallback(async (data, { cancelled } = {}) => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const { synced, blobOpts } = getPreviewBlobOptions(data);
      const blob = await buildLabelsLabQuotePdfBlob(synced, blobOpts);
      if (cancelled?.()) return;
      if (!blob || blob.size === 0) {
        throw new Error('PDF preview was empty. Check quote data and try again.');
      }
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setDirty(false);
    } catch (error) {
      if (cancelled?.()) return;
      console.error(error);
      setPreviewError(error?.message || 'Failed to build PDF preview.');
    } finally {
      if (!cancelled?.()) setPreviewLoading(false);
    }
  }, [getPreviewBlobOptions]);

  useEffect(() => {
    let active = true;
    refreshPreview(payload, { cancelled: () => !active });
    return () => {
      active = false;
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const patchPayload = (patch) => {
    const next = { ...payload, ...patch };
    onPayloadChange(next);
    setDirty(true);
    return next;
  };

  const patchProduct = (patch) => {
    const nextProducts = [...products];
    nextProducts[activeProductIndex] = { ...product, ...patch };
    const next = { ...payload, products: nextProducts };
    onPayloadChange(next);
    setDirty(true);
    return next;
  };

  const patchFields = (patch) => {
    patchProduct({ fields: { ...fields, ...patch } });
  };

  const updatePricingRow = (index, key, value) => {
    const rows = [...(product.pdfPricing || [])];
    rows[index] = recalcPricingRow({ ...rows[index], [key]: value });
    patchProduct({ pdfPricing: rows, quantitiesCount: rows.length });
  };

  const updateBoxShippingCell = (index, value) => {
    const rows = [...(product.pdfPricing || [])];
    rows[index] = { ...rows[index], shippingCost: value, shippingCostManual: true };
    patchProduct({ pdfPricing: rows });
  };

  const addPricingTier = () => {
    const rows = [...(product.pdfPricing || []), createEmptyPricingRow(isLabel)];
    patchProduct({ pdfPricing: rows, quantitiesCount: rows.length });
  };

  const removePricingTier = (index) => {
    const rows = (product.pdfPricing || []).filter((_, i) => i !== index);
    if (!rows.length) return;
    patchProduct({ pdfPricing: rows, quantitiesCount: rows.length });
  };

  const updateShippingRow = (index, key, value) => {
    const rows = [...(product.pdfShipping || [])];
    const row = { ...rows[index] };
    const tierCount = product.pdfPricing?.length || 3;

    if (key === 'cost') {
      rows[index] = recalcShippingRow({ ...row, baseCost: value, costs: [value] }, tierCount);
    } else {
      row[key] = value;
      rows[index] = row;
    }
    patchProduct({ pdfShipping: rows });
  };

  const addShippingRow = () => {
    patchProduct({
      pdfShipping: [
        ...(product.pdfShipping || []),
        { method: 'Express Air shipping', baseCost: '', costs: [''], timeframe: '4-5 business days' },
      ],
      showShippingTable: true,
    });
  };

  const removeShippingRow = (index) => {
    const rows = (product.pdfShipping || []).filter((_, i) => i !== index);
    patchProduct({
      pdfShipping: rows,
      ...(rows.length ? {} : {
        showShippingTable: false,
        pdfTurnaround: product.pdfTurnaround || DEFAULT_BOX_TURNAROUND,
      }),
    });
  };

  const toggleShippingTable = (enabled) => {
    patchProduct({
      showShippingTable: enabled,
      pdfTurnaround: enabled ? product.pdfTurnaround : (product.pdfTurnaround || DEFAULT_BOX_TURNAROUND),
    });
  };

  const setCustomTables = (tables) => {
    patchPayload({ pdfCustomTables: tables });
  };

  const addCustomTable = () => {
    setCustomTables([...customTables, createCustomTable({ rows: 3, cols: 3, title: 'Custom table' })]);
  };

  const updateCustomTableAt = (index, table) => {
    const next = [...customTables];
    next[index] = table;
    setCustomTables(next);
  };

  const removeCustomTableAt = (index) => {
    setCustomTables(customTables.filter((_, i) => i !== index));
  };

  const addShippingDetailsTemplate = () => {
    const tierCount = product.pdfPricing?.length || 3;
    const tierHeaders = Array.from({ length: tierCount }, (_, i) => `Tier ${i + 1}`);
    const colCount = 1 + tierCount + 1;
    const headerRow = ['Method', ...tierHeaders, 'Timeframe'];
    const dataRows = (product.pdfShipping || []).map((row) => {
      const costs = [...(row.costs || [])];
      while (costs.length < tierCount) costs.push('');
      return [row.method || '', ...costs.slice(0, tierCount), row.timeframe || ''];
    });
    const rows = [headerRow, ...dataRows];
    while (rows.length < 3) {
      rows.push(Array.from({ length: colCount }, () => ''));
    }
    setCustomTables([
      ...customTables,
      {
        ...createCustomTable({
          rows: rows.length,
          cols: colCount,
          title: 'Shipping details',
          headerRow: true,
        }),
        cells: rows,
      },
    ]);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const synced = syncPayloadFromEdits(payload);
      await refreshPreview(synced);
      await downloadLabelsLabQuotePdfs(synced);
    } finally {
      setDownloading(false);
    }
  };

  const switchProduct = async (index) => {
    setActiveProductIndex(index);
    if (payload.pdfOutputMode === 'separate' && products.length > 1) {
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const synced = syncPayloadFromEdits(payload);
        const blob = await buildLabelsLabQuotePdfBlob(synced, { productIndices: [index] });
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch (error) {
        console.error(error);
        setPreviewError(error?.message || 'Failed to build PDF preview.');
      } finally {
        setPreviewLoading(false);
      }
    } else {
      setDirty(true);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Review &amp; edit PDF</h2>
          {multiProduct && (
            <p className="text-xs text-gray-500 mt-1">
              {payload.pdfOutputMode === 'separate'
                ? `Separate PDFs — editing product ${activeProductIndex + 1} of ${products.length}`
                : `One PDF — ${products.length} pages`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
            Back to specs
          </button>
          <button
            type="button"
            onClick={() => refreshPreview(payload)}
            disabled={previewLoading || !dirty}
            className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
          >
            {previewLoading ? 'Updating…' : 'Update preview'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || previewLoading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
          >
            {downloading ? 'Downloading…' : multiProduct && payload.pdfOutputMode === 'separate'
              ? `Download ${products.length} PDFs`
              : 'Download PDF'}
          </button>
        </div>
      </div>

      {dirty && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          You have unsaved edits — click <strong>Update preview</strong> to refresh the PDF.
        </p>
      )}

      {multiProduct && (
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
          {products.map((p, index) => (
            <button
              key={index}
              type="button"
              onClick={() => switchProduct(index)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                index === activeProductIndex
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {getProductTabLabel(p, index, products)}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-5 max-h-[80vh] overflow-y-auto pr-1">
          <section className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Quote header</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Client</label>
                <input className={inputClass} value={payload.companyName || ''} onChange={(e) => patchPayload({ companyName: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Attention to</label>
                <input className={inputClass} value={payload.attentionTo || ''} onChange={(e) => patchPayload({ attentionTo: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Prepared by</label>
                <input className={inputClass} value={payload.salesRepName || ''} onChange={(e) => patchPayload({ salesRepName: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Project ID</label>
                <input className={inputClass} value={payload.projectId || ''} onChange={(e) => patchPayload({ projectId: e.target.value })} placeholder="e.g. LL1001" />
              </div>
              <div>
                <label className={labelClass}>Date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={payload.quoteDate || ''}
                  onChange={(e) => patchPayload({ quoteDate: e.target.value })}
                />
              </div>
              <div>
                <label className={labelClass}>Valid until</label>
                <input
                  type="date"
                  className={inputClass}
                  value={payload.validUntil || ''}
                  onChange={(e) => patchPayload({ validUntil: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Product description</label>
                <input className={inputClass} value={payload.projectDescription || ''} onChange={(e) => patchPayload({ projectDescription: e.target.value })} />
              </div>
            </div>
          </section>

          <CollapsibleSection
            title="Layout position & size"
            subtitle="Click Show to open — arrows move sections (5pt), Width/Height +/- (10pt)"
          >
            <p className="text-xs text-gray-500 mb-3">
              Click <strong>Update preview</strong> after layout changes.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <PdfLayoutJoystick
                sectionLabel="Logo"
                offset={pdfLayout.logo}
                onNudge={(dx, dy) => nudgeLayout('logo', dx, dy)}
                onReset={() => resetLayoutSection('logo')}
                enableSize
                onResize={(dw, dh) => resizeLayout('logo', dw, dh)}
              />
              <PdfLayoutJoystick
                sectionLabel="Client info"
                offset={pdfLayout.meta}
                onNudge={(dx, dy) => nudgeLayout('meta', dx, dy)}
                onReset={() => resetLayoutSection('meta')}
                enableSize
                onResize={(dw, dh) => resizeLayout('meta', dw, dh)}
              />
              <PdfLayoutJoystick
                sectionLabel={isBox ? 'Box styles' : isLabel ? 'Label styles' : 'Style chart'}
                offset={pdfLayout.styleSheet}
                onNudge={(dx, dy) => nudgeLayout('styleSheet', dx, dy)}
                onReset={() => resetLayoutSection('styleSheet')}
                enableSize
                onResize={(dw, dh) => resizeLayout('styleSheet', dw, dh)}
              />
              <PdfLayoutJoystick
                sectionLabel="Specs details"
                offset={pdfLayout.details}
                onNudge={(dx, dy) => nudgeLayout('details', dx, dy)}
                onReset={() => resetLayoutSection('details')}
                enableSize
                onResize={(dw, dh) => resizeLayout('details', dw, dh)}
              />
              <PdfLayoutJoystick
                sectionLabel="Product description"
                offset={pdfLayout.productDescription}
                onNudge={(dx, dy) => nudgeLayout('productDescription', dx, dy)}
                onReset={() => resetLayoutSection('productDescription')}
                enableSize
                onResize={(dw, dh) => resizeLayout('productDescription', dw, dh)}
              />
              <PdfLayoutJoystick
                sectionLabel="Pricing table"
                offset={pdfLayout.pricingTable}
                onNudge={(dx, dy) => nudgeLayout('pricingTable', dx, dy)}
                onReset={() => resetLayoutSection('pricingTable')}
                enableSize
                onResize={(dw, dh) => resizeLayout('pricingTable', dw, dh)}
              />
              {!isLabel && (
                <PdfLayoutJoystick
                  sectionLabel="Shipping table"
                  offset={pdfLayout.shippingTable}
                  onNudge={(dx, dy) => nudgeLayout('shippingTable', dx, dy)}
                  onReset={() => resetLayoutSection('shippingTable')}
                  enableSize
                  onResize={(dw, dh) => resizeLayout('shippingTable', dw, dh)}
                />
              )}
              <PdfLayoutJoystick
                sectionLabel="Custom tables (all)"
                offset={pdfLayout.customTables}
                onNudge={(dx, dy) => nudgeLayout('customTables', dx, dy)}
                onReset={() => resetLayoutSection('customTables')}
                enableSize
                onResize={(dw, dh) => resizeLayout('customTables', dw, dh)}
              />
            </div>
          </CollapsibleSection>

          <section className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Product details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={labelClass}>Specs line (pricing table)</label>
                <input className={inputClass} value={product.pdfSpecsLine || ''} onChange={(e) => patchProduct({ pdfSpecsLine: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>{isBox ? 'Box style #' : isLabel ? 'Label shape' : 'Style #'}</label>
                <input
                  className={inputClass}
                  value={isLabel ? (fields.shape || '') : (fields.bagType || fields.boxType || '')}
                  onChange={(e) => {
                    if (isBox) patchFields({ boxType: e.target.value });
                    else if (isLabel) patchFields({ shape: e.target.value });
                    else patchFields({ bagType: e.target.value });
                  }}
                />
              </div>
              {!isLabel && (
                <div>
                  <label className={labelClass}>SKU count</label>
                  <input type="number" min="1" className={inputClass} value={product.SKU || 1} onChange={(e) => patchProduct({ SKU: parseInt(e.target.value, 10) || 1 })} />
                </div>
              )}
              <div>
                <label className={labelClass}>Material</label>
                <input className={inputClass} value={fields.material || ''} onChange={(e) => patchFields({ material: e.target.value })} />
              </div>
              {isLabel && (
                <>
                  <div>
                    <label className={labelClass}>Thickness</label>
                    <input className={inputClass} value={fields.thickness || ''} onChange={(e) => patchFields({ thickness: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelClass}>Rolling direction</label>
                    <input className={inputClass} value={fields.rollingDirection || ''} onChange={(e) => patchFields({ rollingDirection: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelClass}>Special effects</label>
                    <input className={inputClass} value={fields.specialEffects || ''} onChange={(e) => patchFields({ specialEffects: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelClass}>Shipping method</label>
                    <input className={inputClass} value={fields.shippingMethod || ''} onChange={(e) => patchFields({ shippingMethod: e.target.value })} />
                  </div>
                </>
              )}
              <div>
                <label className={labelClass}>Finish</label>
                <input className={inputClass} value={fields.printingFinishes || ''} onChange={(e) => patchFields({ printingFinishes: e.target.value })} />
              </div>
              {!isBox && !isLabel && (
                <>
                  <div>
                    <label className={labelClass}>Zipper</label>
                    <input className={inputClass} value={fields.zipperType || ''} onChange={(e) => patchFields({ zipperType: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelClass}>Loading side</label>
                    <input className={inputClass} value={fields.loadingSide || 'Top'} onChange={(e) => patchFields({ loadingSide: e.target.value })} />
                  </div>
                </>
              )}
            </div>
          </section>

          <section className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">Pricing tiers</h3>
              <button type="button" onClick={addPricingTier} className="text-xs text-indigo-600 hover:underline">
                + Add tier
              </button>
            </div>
            <div className="space-y-3">
              {(product.pdfPricing || []).map((row, index) => (
                <div key={index} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500">Tier {index + 1}</span>
                    {(product.pdfPricing || []).length > 1 && (
                      <button type="button" onClick={() => removePricingTier(index)} className="text-xs text-red-600 hover:underline">
                        Remove
                      </button>
                    )}
                  </div>
                  <div className={`grid gap-2 ${isLabel ? 'grid-cols-2 sm:grid-cols-5' : isBox ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`}>
                    {isLabel && (
                      <div className="sm:col-span-2">
                        <label className={labelClass}>Specs line</label>
                        <input
                          className={inputClass}
                          value={row.specsLine ?? ''}
                          onChange={(e) => {
                            const rows = [...(product.pdfPricing || [])];
                            rows[index] = { ...rows[index], specsLine: e.target.value };
                            patchProduct({ pdfPricing: rows });
                          }}
                        />
                      </div>
                    )}
                    <div>
                      <label className={labelClass}>QTY</label>
                      <input type="number" className={inputClass} value={row.qty ?? ''} onChange={(e) => updatePricingRow(index, 'qty', Number(e.target.value))} />
                    </div>
                    <div>
                      <label className={labelClass}>{isLabel ? 'SKU label' : 'SKU'}</label>
                      {isLabel ? (
                        <input
                          className={inputClass}
                          value={row.skuLabel ?? ''}
                          onChange={(e) => {
                            const rows = [...(product.pdfPricing || [])];
                            rows[index] = { ...rows[index], skuLabel: e.target.value };
                            patchProduct({ pdfPricing: rows });
                          }}
                        />
                      ) : (
                        <input className={inputClass} value={product.SKU || ''} readOnly />
                      )}
                    </div>
                    <div>
                      <label className={labelClass}>Price / unit</label>
                      <input type="number" step="0.0001" className={inputClass} value={row.pricePerUnit ?? ''} onChange={(e) => updatePricingRow(index, 'pricePerUnit', Number(e.target.value))} />
                    </div>
                    <div>
                      <label className={labelClass}>Order total</label>
                      <input className={inputClass} value={row.orderTotal != null ? row.orderTotal.toFixed(2) : ''} readOnly />
                    </div>
                    {isBox && (
                      <div>
                        <label className={labelClass}>Shipping column</label>
                        <input
                          className={inputClass}
                          value={row.shippingCost ?? ''}
                          placeholder="Leave blank if none"
                          onChange={(e) => updateBoxShippingCell(index, e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="border border-gray-200 rounded-lg p-4 space-y-3">
            {isLabel ? (
              <>
                <h3 className="font-semibold text-gray-800">Order lead time</h3>
                <textarea
                  rows={2}
                  className={inputClass}
                  value={product.pdfOrderLeadTime ?? ''}
                  placeholder={DEFAULT_LABEL_LEAD_TIME}
                  onChange={(e) => patchProduct({ pdfOrderLeadTime: e.target.value })}
                />
                <p className="text-xs text-gray-400">Leave blank to hide on PDF.</p>
              </>
            ) : (
              <>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Shipping &amp; turnaround</h3>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={showShippingTable}
                  onChange={(e) => toggleShippingTable(e.target.checked)}
                />
                Show shipping table (like bags)
              </label>
            </div>

            {showShippingTable ? (
              <>
                <p className="text-xs text-gray-500">
                  Full shipping table on PDF. Tier 2 &amp; 3 auto-calculate from base (×1.333333, ×2).
                </p>
                <div className="space-y-3">
                  {(product.pdfShipping || []).map((row, index) => (
                    <div key={index} className="border border-gray-100 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-gray-500">Method {index + 1}</span>
                        <button type="button" onClick={() => removeShippingRow(index)} className="text-xs text-red-600 hover:underline">
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className={labelClass}>Method</label>
                          <input className={inputClass} value={row.method || ''} onChange={(e) => updateShippingRow(index, 'method', e.target.value)} />
                        </div>
                        <div>
                          <label className={labelClass}>Base cost (Tier 1)</label>
                          <input className={inputClass} value={row.baseCost ?? ''} onChange={(e) => updateShippingRow(index, 'cost', e.target.value)} />
                        </div>
                        <div className="sm:col-span-2">
                          <label className={labelClass}>Timeframe</label>
                          <input className={inputClass} value={row.timeframe || ''} onChange={(e) => updateShippingRow(index, 'timeframe', e.target.value)} />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">Tiers: {(row.costs || []).join(' → ') || '—'}</p>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addShippingRow} className="text-xs text-indigo-600 hover:underline">
                  + Add shipping method
                </button>
              </>
            ) : (
              <div>
                <label className={labelClass}>
                  Turnaround text {isBox ? '(shown on box PDF instead of shipping table)' : '(optional)'}
                </label>
                <textarea
                  rows={3}
                  className={inputClass}
                  value={product.pdfTurnaround ?? ''}
                  placeholder={'Printing & Shipping turnaround:\n15-20 business days'}
                  onChange={(e) => patchProduct({ pdfTurnaround: e.target.value })}
                />
                <p className="text-xs text-gray-400 mt-1">Leave blank to hide on PDF.</p>
              </div>
            )}
              </>
            )}
          </section>

          <section className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-800">Custom tables</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Add your own tables (any rows × columns). Shown on the PDF after pricing/shipping.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {!isLabel && (product.pdfShipping || []).length > 0 && (
                  <button
                    type="button"
                    onClick={addShippingDetailsTemplate}
                    className="text-xs text-indigo-600 hover:underline"
                  >
                    From shipping data
                  </button>
                )}
                <button type="button" onClick={addCustomTable} className="text-xs text-indigo-600 hover:underline">
                  + New table
                </button>
              </div>
            </div>

            {customTables.length === 0 ? (
              <p className="text-xs text-gray-400 italic">
                No custom tables yet. Click &quot;+ New table&quot; to create e.g. a 3×3 shipping details grid.
              </p>
            ) : (
              <div className="space-y-4">
                {customTables.map((table, index) => (
                  <PdfCustomTableEditor
                    key={table.id}
                    table={table}
                    index={index}
                    onChange={(next) => updateCustomTableAt(index, next)}
                    onRemove={() => removeCustomTableAt(index)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-100 min-h-[600px]">
          {previewLoading && !previewUrl && (
            <div className="flex items-center justify-center h-[600px] text-gray-500">Building preview…</div>
          )}
          {previewError && !previewUrl && !previewLoading && (
            <div className="flex flex-col items-center justify-center h-[600px] px-6 text-center text-red-600 text-sm gap-3">
              <p>{previewError}</p>
              <button
                type="button"
                onClick={() => refreshPreview(payload)}
                className="px-4 py-2 bg-white border border-red-200 rounded-lg text-sm hover:bg-red-50"
              >
                Retry preview
              </button>
            </div>
          )}
          {!previewLoading && !previewUrl && !previewError && (
            <div className="flex items-center justify-center h-[600px] text-gray-500 text-sm">
              No preview yet — click Update preview.
            </div>
          )}
          {previewUrl && (
            <object
              title="Quote PDF preview"
              data={previewUrl}
              type="application/pdf"
              className="w-full h-[80vh] min-h-[600px] bg-white"
            >
              <iframe title="Quote PDF preview" src={previewUrl} className="w-full h-[80vh] min-h-[600px] bg-white" />
            </object>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuotePdfPreviewPanel;
